import React, { Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react';
import { Search as SearchIcon } from 'lucide-react';
import { Input } from 'src/shared/components/ui/input';
import StandardAppLayout from 'src/shared/components/layout/standard-app-layout/StandardAppLayout';
import Alert from 'src/shared/components/ui/alert';
import {
    useGetRiskInsightsSitesQuery,
    useGetSiteFilterOptionsQuery,
    type RiskInsightsSitesSortField,
    type RiskInsightsSitesSortDirection,
} from 'src/app/portfolio/state/api/riskInsightsEngineSlice';
import TimePeriodFilter from 'src/shared/components/filters/time-period-filter/TimePeriodFilter';
import useGnrFilters from 'src/app/portfolio/views/global-nature-risk/hooks/useGnrFilters';
import useGnrAvailableTimePeriods from 'src/app/portfolio/views/global-nature-risk/hooks/useGnrAvailableTimePeriods';
import GlobalNatureRiskGate from 'src/app/portfolio/views/global-nature-risk/GlobalNatureRiskGate';
import Loader from 'src/shared/components/loader/Loader';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import DataTable from 'src/shared/components/data-table/DataTable';
import type { DataTableColumns, DataTableState, RowsPerPage } from 'src/shared/components/data-table/dataTableTypes';
import { formatAssessmentDate } from 'src/app/portfolio/utils/formatAssessmentDate';
import { formatIsicActivityLines } from 'src/app/portfolio/utils/formatIsicActivityLines';
import RiskScoreCell from 'src/shared/components/risk-score-cell/RiskScoreCell';
import { SortingDirection } from 'src/shared/components/data-table/dataTableTypes';
import type { RiskInsightsSiteRow } from 'src/app/portfolio/state/api/riskInsightsSitesQuery';
import { sanitizeSelectedFilters, areSelectedFiltersEqual } from 'src/app/portfolio/utils/sanitizeGnrFilters';
import TruncatedText from 'src/shared/components/ui/TruncatedText';
import ListMapViewTabs from 'src/shared/components/list-map-view-tabs/ListMapViewTabs';
import styles from './SiteList.module.scss';

import SiteListFilters from './components/filters/SiteListFilters';
import GnrActiveFilters from './components/filters/GnrActiveFilters';
import SiteCell from './SiteCell';
import SiteListDownloadButton from './components/download/SiteListDownloadButton';
import { useSiteListDownload, PROGRESS_BAR_THRESHOLD } from './components/download/useSiteListDownload';
import useSiteExplorerView from '../siteExplorerView';
import { siteEstateFiltersOf } from '../siteEstateFilters';

// MapLibre is ~275 kB gzipped. Behind the tab, it loads only for a user who opens
// the map. A static import puts it in the entry chunk, on every page. See ADR-003.
// CAUTION: keep this lazy. A static import puts MapLibre (275 KB gzip) in the entry
// chunk, where every page loads it.
const SiteExplorerMapView = lazy(() => import('./components/map-view/SiteExplorerMapView'));

const COLUMN_TO_SORT_FIELD: Record<string, RiskInsightsSitesSortField | undefined> = {
    siteName: 'NAME',
    physicalNatureRisk: 'PHYSICAL_NATURE_RISK',
    operationalDependencies: 'OPERATIONAL_DEPENDENCIES',
    impactsOnNature: 'IMPACTS_ON_NATURE',
    stateOfNature: 'STATE_OF_NATURE',
    assessmentDate: 'ASSESSMENT_DATE',
};

const SORT_DIRECTION_TO_BACKEND: Partial<Record<SortingDirection, RiskInsightsSitesSortDirection>> = {
    [SortingDirection.ASC]: 'ASC',
    [SortingDirection.DESC]: 'DESC',
};

const DEFAULT_ROWS_PER_PAGE = 20 as RowsPerPage;

const ActivityCell = ({ activity }: { activity: RiskInsightsSiteRow['activity'] }) => {
    const lines = formatIsicActivityLines(activity);
    if (!lines) {
        return <span className='text-muted-foreground'>—</span>;
    }

    return (
        <div className='flex flex-col gap-0.5 text-sm min-w-0' data-testid='site-list-activity-cell'>
            <TruncatedText text={lines.sectionLine} tooltipText={lines.sectionTooltip} className='text-black text-sm' forceTooltip={true} />
            <TruncatedText text={lines.classLine} tooltipText={lines.classTooltip} className='text-grey-80 text-xs' forceTooltip={true} />
        </div>
    );
};

const SiteListContent = () => {
    const { currentCustomerId } = useCustomer();
    const { view, setView } = useSiteExplorerView();

    const {
        selectedFilters,
        applyFilters,
        resetFilters,
        resetFiltersAndPeriod,
        updateFilters,
        search,
        setSearch,
        sort,
        sortDir,
        page: urlPage,
        setPage,
        setSort,
        selectedAt,
        setSelectedAt,
    } = useGnrFilters();

    const [tableState, setTableState] = useState<Partial<DataTableState>>({
        fixedHeader: true,
        rowsPerPage: DEFAULT_ROWS_PER_PAGE,
        currentPageNumber: urlPage,
        sortedColumn: sort && sortDir ? { columnId: sort, sortedDirection: sortDir } : null,
        data: [],
    });
    const [searchInputValue, setSearchInputValue] = useState(search);
    const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        setSearchInputValue(search);
    }, [search]);

    useEffect(() => {
        setTableState(prev => ({
            ...prev,
            sortedColumn: sort && sortDir ? { columnId: sort, sortedDirection: sortDir } : null,
        }));
    }, [sort, sortDir]);

    useEffect(() => {
        return () => {
            if (debounceTimer.current) clearTimeout(debounceTimer.current);
        };
    }, []);

    const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchInputValue(value);
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(() => {
            setSearch(value);
        }, 300);
    };
    const sortBy = tableState.sortedColumn ? COLUMN_TO_SORT_FIELD[tableState.sortedColumn.columnId] : undefined;
    const sortDirForBackend = tableState.sortedColumn ? SORT_DIRECTION_TO_BACKEND[tableState.sortedColumn.sortedDirection] : undefined;

    const prevSearchRef = useRef(search);
    const prevSortByRef = useRef(sortBy);
    const prevSortDirRef = useRef(sortDirForBackend);
    useEffect(() => {
        const changed =
            search !== prevSearchRef.current || sortBy !== prevSortByRef.current || sortDirForBackend !== prevSortDirRef.current;
        prevSearchRef.current = search;
        prevSortByRef.current = sortBy;
        prevSortDirRef.current = sortDirForBackend;
        if (changed) {
            setTableState(prev => ({ ...prev, currentPageNumber: 1 }));
        }
    }, [search, sortBy, sortDirForBackend]);

    useEffect(() => {
        setTableState(prev => ({ ...prev, currentPageNumber: urlPage }));
    }, [urlPage]);

    const page = urlPage;
    const rowsPerPage =
        tableState.rowsPerPage !== undefined && tableState.rowsPerPage !== Infinity ? tableState.rowsPerPage : DEFAULT_ROWS_PER_PAGE;

    const { data: filterOptionsData, isLoading: isLoadingFilterOptions } = useGetSiteFilterOptionsQuery(
        { businessCentralId: currentCustomerId },
        { skip: !currentCustomerId }
    );
    const siteFilterOptions = filterOptionsData?.organisation?.siteFilterOptions;
    const validatedFilters = useMemo(
        () => sanitizeSelectedFilters(selectedFilters, siteFilterOptions),
        [selectedFilters, siteFilterOptions]
    );
    // One object for both views: the query below reads it as its arguments, and the
    // map writes the same fields into the tile URL.
    const estateFilters = useMemo(
        () => siteEstateFiltersOf({ selectedFilters: validatedFilters, search, at: selectedAt }),
        [validatedFilters, search, selectedAt]
    );

    useEffect(() => {
        if (!siteFilterOptions || areSelectedFiltersEqual(validatedFilters, selectedFilters)) {
            return;
        }
        applyFilters(validatedFilters);
    }, [applyFilters, selectedFilters, siteFilterOptions, validatedFilters]);

    const { timePeriods } = useGnrAvailableTimePeriods(currentCustomerId);

    const {
        currentData: sitesResult,
        isFetching: isSitesFetching,
        isError: isSitesError,
        error: sitesError,
    } = useGetRiskInsightsSitesQuery(
        {
            businessCentralId: currentCustomerId,
            page,
            pageSize: rowsPerPage,
            sortBy,
            sortDir: sortDirForBackend,
            ...estateFilters,
        },
        { skip: !currentCustomerId }
    );

    const sitesConnection = sitesResult?.organisation?.sites;
    const totalItems = sitesConnection?.pagination.totalItems ?? 0;

    useEffect(() => {
        if (!sitesConnection) {
            // During a refetch (e.g. paginating after a sort change) RTK
            // Query's currentData briefly becomes undefined. Keep the previous
            // rows in the table so the user sees the page transition under the
            // loading overlay instead of an empty table.
            return;
        }

        const tableData: DataTableState['data'] = sitesConnection.items.map(item => [
            <SiteCell
                key={`${item.id}-site`}
                name={item.name}
                countryName={item.geography?.countryName ?? null}
                siteId={item.id}
                customerId={currentCustomerId}
            />,
            <ActivityCell key={`${item.id}-activity`} activity={item.activity} />,
            <RiskScoreCell
                key={`${item.id}-physical-nature-risk`}
                value={item.riskSummary?.physicalNatureRisk ?? null}
                direction='higher-worse'
            />,
            <RiskScoreCell
                key={`${item.id}-operational-dependencies`}
                value={item.riskSummary?.operationalDependencies ?? null}
                direction='higher-worse'
            />,
            <RiskScoreCell
                key={`${item.id}-impacts-on-nature`}
                value={item.riskSummary?.impactsOnNature ?? null}
                direction='higher-worse'
            />,
            <RiskScoreCell key={`${item.id}-state-of-nature`} value={item.riskSummary?.stateOfNature ?? null} direction='higher-better' />,
            item.riskSummary ? (
                <span key={`${item.id}-assessment-date`} className='text-sm text-grey-90'>
                    {formatAssessmentDate(item.riskSummary.assessmentDate)}
                </span>
            ) : (
                <span key={`${item.id}-assessment-date`} className='text-sm text-muted-foreground'>
                    —
                </span>
            ),
        ]);

        setTableState(prev => ({
            ...prev,
            data: tableData,
            currentPageNumber: sitesConnection.pagination.page,
            rowsPerPage: sitesConnection.pagination.pageSize as RowsPerPage,
        }));
    }, [sitesConnection]);

    const columns: DataTableColumns = useMemo(
        () => [
            {
                columnId: 'siteName',
                title: 'Site',
                width: '200px',
            },
            {
                columnId: 'activity',
                title: 'Primary Activity',
                width: '200px',
            },
            {
                columnId: 'physicalNatureRisk',
                title: 'Physical Nature Risk',
                width: '140px',
                isSortable: true,
            },
            {
                columnId: 'operationalDependencies',
                title: 'Dependencies',
                width: '140px',
                isSortable: true,
            },
            {
                columnId: 'impactsOnNature',
                title: 'Impacts on Nature',
                width: '140px',
                isSortable: true,
            },
            {
                columnId: 'stateOfNature',
                title: 'State of Nature',
                width: '140px',
                isSortable: true,
            },
            {
                columnId: 'assessmentDate',
                title: 'Assessment Date',
                width: '140px',
            },
        ],
        []
    );

    const handleStateChange = (newState: DataTableState) => {
        setTableState(newState);
        if (newState.currentPageNumber !== tableState.currentPageNumber) {
            setPage(newState.currentPageNumber);
        }

        if (
            newState.sortedColumn?.columnId !== tableState.sortedColumn?.columnId ||
            newState.sortedColumn?.sortedDirection !== tableState.sortedColumn?.sortedDirection
        ) {
            const direction = newState.sortedColumn?.sortedDirection;
            if (newState.sortedColumn?.columnId && (direction === SortingDirection.ASC || direction === SortingDirection.DESC)) {
                setSort(newState.sortedColumn.columnId, direction);
            } else {
                setSort(null, null);
            }
        }
    };

    const { download, isDownloading, downloadError, downloadProgress } = useSiteListDownload(currentCustomerId, selectedAt ?? undefined);

    // Both views show the filters that narrow them. Over the map it floats, there
    // being no list to read them from; in the list it sticks to the top as the rows
    // scroll under it.
    const activeFilters = (layout: 'inline' | 'overlay') => (
        <GnrActiveFilters
            selectedFilters={validatedFilters}
            countries={siteFilterOptions?.countries}
            isicCodes={siteFilterOptions?.isicCodes}
            onRemove={updateFilters}
            onClearAll={resetFiltersAndPeriod}
            matchingSiteCount={sitesConnection ? totalItems : undefined}
            selectedPeriodLabel={timePeriods.find(p => p.value === selectedAt)?.label ?? null}
            onClearPeriod={() => setSelectedAt(null)}
            // The list pane has a search box that already shows the term. Over the
            // map there is none, so there the pill is the only thing that says why
            // a site is missing.
            searchTerm={layout === 'overlay' ? search : undefined}
            onClearSearch={() => setSearch('')}
            layout={layout}
        />
    );

    const sitesErrorMessage =
        sitesError && typeof sitesError === 'object' && 'message' in sitesError && typeof sitesError.message === 'string'
            ? sitesError.message
            : 'Could not load sites from the risk insights service.';

    return (
        <StandardAppLayout
            title='Nature Risk Site Explorer'
            stickyHeader={false}
            classNames={{ mainContent: styles.mainContentOverride, topContent: '!pt-2 !pb-0', otherActions: 'p-0 pt-1 justify-normal' }}
            otherActions={
                <div className='flex items-center gap-3'>
                    <TimePeriodFilter periods={timePeriods} selectedValue={selectedAt} onSelect={setSelectedAt} />
                    <SiteListFilters
                        selectedFilters={validatedFilters}
                        onApply={applyFilters}
                        onClear={resetFilters}
                        filterOptions={siteFilterOptions}
                        isLoadingFilterOptions={isLoadingFilterOptions}
                        businessCentralId={currentCustomerId ?? undefined}
                    />
                </div>
            }
            mainContent={
                <div className={styles.explorerContainer} data-testid='site-list-main'>
                    <div className={styles.headerRow} data-testid='site-list-header-row'>
                        <ListMapViewTabs tab={view} onTabChange={setView} isMapFirst />
                    </div>
                    {view === 'map' ? (
                        <div className={styles.mapPane}>
                            {activeFilters('overlay')}
                            <Suspense fallback={<Loader />}>
                                <SiteExplorerMapView filters={estateFilters} />
                            </Suspense>
                        </div>
                    ) : (
                        <div className={styles.listPane}>
                            {activeFilters('inline')}
                            <div className='flex items-center gap-3' data-testid='site-list-controls'>
                                <div className='relative w-80' data-testid='site-list-search'>
                                    <SearchIcon className='w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none' />
                                    <Input
                                        roundness='Round'
                                        type='search'
                                        value={searchInputValue}
                                        onChange={onSearchChange}
                                        placeholder='Search sites ...'
                                        aria-label='Search sites'
                                        data-testid='site-list-search-input'
                                        className='pl-9'
                                    />
                                </div>
                                <div className='ml-auto'>
                                    <SiteListDownloadButton
                                        onClick={download}
                                        disabled={!currentCustomerId}
                                        isDownloading={isDownloading}
                                        progress={downloadProgress}
                                        showLargePortfolioWarning={totalItems >= PROGRESS_BAR_THRESHOLD}
                                    />
                                </div>
                            </div>

                            {!currentCustomerId ? (
                                <Alert severity='info'>Select a customer to load portfolio sites.</Alert>
                            ) : (
                                <>
                                    {isSitesError && (
                                        <Alert severity='error' data-testid='site-list-sites-error'>
                                            {sitesErrorMessage}
                                        </Alert>
                                    )}

                                    {downloadError && (
                                        <Alert severity='error' data-testid='site-list-download-error'>
                                            {downloadError}
                                        </Alert>
                                    )}

                                    <div className='flex flex-col flex-1 min-h-0 pb-2' data-testid='site-list-sites-table'>
                                        <DataTable
                                            state={tableState}
                                            columns={columns}
                                            onStateChange={handleStateChange}
                                            emptyStateMessage={
                                                isSitesFetching ? (
                                                    'Loading sites…'
                                                ) : isSitesError ? (
                                                    'Unable to load sites.'
                                                ) : (
                                                    <div className='flex flex-col items-center gap-2 py-11 w-full'>
                                                        <p className='text-xl'>No results found</p>
                                                        <p className='text-base text-muted-foreground'>Try resetting your filters.</p>
                                                    </div>
                                                )
                                            }
                                            className='rounded-xl flex-1 overflow-hidden flex flex-col w-full'
                                            tableClassName='table-fixed'
                                            totalRowsForPagination={totalItems}
                                            isServerSideSort
                                            isLoadingOverlay={isSitesFetching}
                                            disableRowHover={totalItems === 1}
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>
            }
        />
    );
};

/** SiteList, shown only to a user who may see Global Nature Risk. */
const SiteList = () => (
    <GlobalNatureRiskGate>
        <SiteListContent />
    </GlobalNatureRiskGate>
);

export default SiteList;
